import { motion } from "framer-motion";
import { Mail, Phone, Instagram, Youtube } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.7, delay: i * 0.15, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

export default function Contact() {
  return (
    <div>
      {/* ========== HERO (DARK) ========== */}
      <section
        data-testid="contact-hero-section"
        className="relative bg-dark_base overflow-hidden"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-32 md:py-40">
          <motion.div initial="hidden" animate="visible" className="max-w-3xl">
            <motion.p variants={fadeUp} custom={0} className="text-xs tracking-[0.2em] uppercase font-bold text-mint mb-6 font-body">
              Work With Us
            </motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="font-heading text-5xl md:text-6xl lg:text-[5rem] leading-[1.05] tracking-tight font-extrabold text-cream mb-8">
              Let's Build Something{" "}
              <span className="font-accent italic font-normal text-mint">Together</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-lg md:text-xl leading-relaxed text-cream/70 max-w-2xl font-body">
              Reach out to us directly. We'd love to hear about your project.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ========== CONTACT INFO (LIGHT) ========== */}
      <section className="bg-light_base -mt-12 rounded-t-[3rem] relative z-10">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="bg-dark_surface rounded-3xl p-10 md:p-16" data-testid="contact-info-container">

            {/* Email */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0 }}
              className="flex items-start gap-5 pb-8 border-b border-white/10"
            >
              <div className="w-12 h-12 bg-mint/15 rounded-full flex items-center justify-center flex-shrink-0">
                <Mail size={22} className="text-mint" />
              </div>
              <div>
                <p className="text-xs tracking-[0.15em] uppercase font-bold text-cream/40 mb-2 font-body">Email</p>
                <a
                  href="mailto:mintmusedigtial@gmail.com"
                  data-testid="contact-email-link"
                  className="font-body text-lg md:text-xl text-cream hover:text-mint transition-colors duration-300"
                >
                  mintmusedigtial@gmail.com
                </a>
              </div>
            </motion.div>

            {/* Phone */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="flex items-start gap-5 py-8 border-b border-white/10"
            >
              <div className="w-12 h-12 bg-mint/15 rounded-full flex items-center justify-center flex-shrink-0">
                <Phone size={22} className="text-mint" />
              </div>
              <div>
                <p className="text-xs tracking-[0.15em] uppercase font-bold text-cream/40 mb-2 font-body">Phone</p>
                <a
                  href="tel:9079857071"
                  data-testid="contact-phone-link"
                  className="font-body text-lg md:text-xl text-cream hover:text-mint transition-colors duration-300"
                >
                  9079857071
                </a>
              </div>
            </motion.div>

            {/* Social */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex items-start gap-5 pt-8"
            >
              <div className="w-12 h-12 bg-mint/15 rounded-full flex items-center justify-center flex-shrink-0">
                <Instagram size={22} className="text-mint" />
              </div>
              <div>
                <p className="text-xs tracking-[0.15em] uppercase font-bold text-cream/40 mb-3 font-body">Follow Us</p>
                <div className="flex gap-4">
                  <a
                    href="https://www.instagram.com/mintmusedigital/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-instagram-link"
                    className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full py-3 px-6 hover:border-mint hover:bg-mint/10 transition-all duration-300"
                  >
                    <Instagram size={18} className="text-cream" />
                    <span className="font-body text-sm text-cream font-semibold">Instagram</span>
                  </a>
                  <a
                    href="https://www.youtube.com/@MintMuseDigital"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-youtube-link"
                    className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full py-3 px-6 hover:border-mint hover:bg-mint/10 transition-all duration-300"
                  >
                    <Youtube size={18} className="text-cream" />
                    <span className="font-body text-sm text-cream font-semibold">YouTube</span>
                  </a>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </section>
    </div>
  );
}
